// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { QuestRoute } from '../../_common/api/QuestRoutes';
import Page from '../pageLayout/Page';
import Title from '../../_common/form/Title';
import Icon from '../../_common/form/Icon';
import Button from '../../_common/form/Button';

// Base URL for the public HostedSolutions API. Inlined by CRA at build time.
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const Heading = styled(Title)`
    text-align: center;
    padding: 0.5em 20px 0;
`;

const Message = styled.div`
    text-align: center;
    padding: 20px;
    font-size: 1.1em;
    line-height: 1.6;
`;

const Status = styled.p`
    text-align: center;
    min-height: 1.4em;
    margin: 0;
    padding: 0 20px 10px;
    font-size: 1.1em;
    line-height: 1.6;
    color: ${({ theme, $error }) => ($error ? theme.error : 'inherit')};

    // &:focus {
    //     outline: 2px solid ${({ theme }) => theme.highlight || theme.tertiary};
    //     outline-offset: 4px;
    //     border-radius: 4px;
    // }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 10px;
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
    display: flex;
    justify-content: center;
    padding: 10px;
    color: ${({ theme }) => theme.tertiary};

    svg {
        animation: ${spin} 0.9s linear infinite;
        font-size: 1.6em;
    }
`;

const VIEW = {
    invalid: 'invalid',
    idle: 'idle',
    submitting: 'submitting',
    success: 'success',
    error: 'error',
};

const HEADING = {
    invalid: { icon: 'exclamationTriangle', text: 'Invalid Confirmation Link' },
    idle: { icon: 'envelope', text: 'Confirm Your Email' },
    submitting: { icon: 'envelope', text: 'Confirm Your Email' },
    success: { icon: 'checkCircle', text: 'Email Confirmed' },
    error: { icon: 'exclamationTriangle', text: 'Confirmation Failed' },
};

const INVALID_LINK_MESSAGE =
    'This confirmation link is invalid. Please use the link from your most recent confirmation email.';
const NETWORK_ERROR_MESSAGE =
    'Something went wrong confirming your email. Please check your connection and try again.';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isValidGuid = (value) => typeof value === 'string' && GUID_PATTERN.test(value);

const ConfirmEmail = () => {
    const [searchParams] = useSearchParams();

    // Read the query string once. useSearchParams (URLSearchParams) already
    // decodes each value exactly once - we never decode it again.
    const [params] = useState(() => ({
        userId: searchParams.get('userId'),
        token: searchParams.get('token'),
    }));

    const paramsValid = isValidGuid(params.userId) && !!params.token;

    const [view, setView] = useState(paramsValid ? VIEW.idle : VIEW.invalid);
    const [feedback, setFeedback] = useState(paramsValid ? '' : INVALID_LINK_MESSAGE);

    // Hard guard against duplicate submissions, independent of render timing.
    const inFlight = useRef(false);
    const statusRef = useRef(null);

    useEffect(() => {
        if (view === VIEW.success || view === VIEW.error) {
            statusRef.current?.focus();
        }
    }, [view]);

    const confirmEmail = () => {
        if (inFlight.current || !paramsValid || view === VIEW.submitting) {
            return;
        }
        inFlight.current = true;
        setView(VIEW.submitting);
        setFeedback('Confirming…');

        // Only fires on an explicit click - never during render/mount/prefetch.
        fetch(`${API_BASE_URL}${QuestRoute.user.confirmEmail}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: params.userId,
                token: params.token,
            }),
        })
            .then(async (res) => {
                let data = null;
                try {
                    data = await res.json();
                } catch {
                    data = null;
                }

                // "Email confirmed." / "Email already confirmed." both succeed
                // and are shown with the same friendly message.
                if (res.ok && data && data.success === true) {
                    setView(VIEW.success);
                    setFeedback('Your email address has been confirmed.');
                    // Strip the sensitive query params from the visible URL.
                    if (window.history && window.history.replaceState) {
                        window.history.replaceState(null, '', window.location.pathname);
                    }
                    return;
                }

                // 400 missing/malformed input or invalid/expired token, 404
                // unknown user, 422 validation - show the server's message.
                if (data && typeof data.errorMessage === 'string' && data.errorMessage) {
                    setView(VIEW.error);
                    setFeedback(data.errorMessage);
                    return;
                }

                throw new Error('Unexpected response');
            })
            .catch(() => {
                // Network or unexpected failure: generic retry, button re-enabled.
                setView(VIEW.idle);
                setFeedback(NETWORK_ERROR_MESSAGE);
            })
            .finally(() => {
                inFlight.current = false;
            });
    };

    const heading = HEADING[view];
    const showConfirmButton =
        view === VIEW.idle || view === VIEW.submitting || view === VIEW.invalid;
    const isError = view === VIEW.error || view === VIEW.invalid;

    return (
        <Page>
            <Heading icon={heading.icon} role='heading' aria-level={1}>
                {heading.text}
            </Heading>

            {(view === VIEW.idle || view === VIEW.submitting) && (
                <Message>
                    Please confirm your email address to finish setting up your account.
                    Select <strong>Confirm Email</strong> below to continue.
                </Message>
            )}

            {view === VIEW.submitting && (
                <Spinner aria-hidden='true'>
                    <Icon icon='spinner' />
                </Spinner>
            )}

            <Status
                ref={statusRef}
                tabIndex={-1}
                role='status'
                aria-live='polite'
                $error={isError}
            >
                {feedback}
            </Status>

            {showConfirmButton && (
                <ButtonContainer>
                    <Button
                        type='button'
                        onClick={confirmEmail}
                        disabled={view !== VIEW.idle}
                        aria-busy={view === VIEW.submitting}
                        icon={view === VIEW.submitting ? 'spinner' : 'envelope'}
                    >
                        {view === VIEW.submitting ? 'Confirming…' : 'Confirm Email'}
                    </Button>
                </ButtonContainer>
            )}

            {(view === VIEW.error || view === VIEW.invalid || view === VIEW.success) && (

                <ButtonContainer>
                    <Button to='/'>Continue</Button>
                </ButtonContainer>

            )}

        </Page>
    );
};

export default ConfirmEmail;
